import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ConstantsProvider } from '@/contexts/ConstantsContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import ChangePassword from '@/pages/ChangePassword';
import TodayQueue from '@/pages/TodayQueue';
import LeadsList from '@/pages/LeadsList';
import LeadDetail from '@/pages/LeadDetail';
import Pipeline from '@/pages/Pipeline';
import ImportLeads from '@/pages/ImportLeads';
import Reports from '@/pages/Reports';
import Scoreboard from '@/pages/Scoreboard';
import Scripts from '@/pages/Scripts';
import Settings from '@/pages/Settings';
import UnassignedLeads from '@/pages/UnassignedLeads';
import AdminDropdowns from '@/pages/AdminDropdowns';
import UserAttendance from '@/pages/UserAttendance';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin, forcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Force password change screen
  if (forcePasswordChange) {
    return <ChangePassword />;
  }

  // Redirect non-admins from admin-only routes
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" />;
  }

  return (
    <ConstantsProvider>
      <Layout>
        {children}
      </Layout>
    </ConstantsProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          } />
          
          <Route path="/" element={
            <ProtectedRoute>
              <TodayQueue />
            </ProtectedRoute>
          } />
          
          <Route path="/leads" element={
            <ProtectedRoute>
              <LeadsList />
            </ProtectedRoute>
          } />
          
          <Route path="/leads/:id" element={
            <ProtectedRoute>
              <LeadDetail />
            </ProtectedRoute>
          } />
          
          <Route path="/pipeline" element={
            <ProtectedRoute>
              <Pipeline />
            </ProtectedRoute>
          } />
          
          <Route path="/import" element={
            <ProtectedRoute adminOnly>
              <ImportLeads />
            </ProtectedRoute>
          } />
          
          <Route path="/reports" element={
            <ProtectedRoute adminOnly>
              <Reports />
            </ProtectedRoute>
          } />
          
          <Route path="/scoreboard" element={
            <ProtectedRoute>
              <Scoreboard />
            </ProtectedRoute>
          } />
          
          <Route path="/scripts" element={
            <ProtectedRoute>
              <Scripts />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute adminOnly>
              <Settings />
            </ProtectedRoute>
          } />
          
          <Route path="/unassigned" element={
            <ProtectedRoute adminOnly>
              <UnassignedLeads />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/dropdowns" element={
            <ProtectedRoute adminOnly>
              <AdminDropdowns />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/attendance" element={
            <ProtectedRoute adminOnly>
              <UserAttendance />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <Toaster position="bottom-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
